import { Badge } from '@/components/ui/shadcn/badge';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useTranslatedLegacySettings } from '@/hooks/useTranslatedLegacySettings';
import { useTranslations } from '@/hooks/useTranslations';
import { formatCurrency } from '@/lib/utils';
import type { Lead } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Building2, Calendar, DollarSign, MapPin, User } from 'lucide-react';
import { useCallback, useRef } from 'react';

interface LeadCardProps {
    lead: Lead;
}

export function LeadCard({ lead }: LeadCardProps) {
    const { legacy } = useTranslatedLegacySettings();
    const { t } = useTranslations();

    const getStatusConfig = (status: string) => {
        return (
            legacy.LEAD_STATUS_OPTIONS.find((option) => option.value === status) || {
                value: status,
                label: status.replace('_', ' ').toUpperCase(),
                color: 'bg-muted text-foreground border-border',
            }
        );
    };

    const getPriorityConfig = (priority: string) => {
        return (
            legacy.LEAD_PRIORITY_OPTIONS.find((option) => option.value === priority) || {
                value: priority,
                label: priority.replace('_', ' ').toUpperCase(),
                color: 'bg-muted text-foreground border-border',
                icon: '',
            }
        );
    };

    const value = lead.estimated_value || lead.value || 0;
    const leadNumber = lead.lead_number || lead.id.slice(0, 8);

    // Track touch events to distinguish scrolls from taps
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const isScrollingRef = useRef(false);

    // Handle touch start - record initial position
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0];
        if (touch) {
            touchStartRef.current = { x: touch.clientX, y: touch.clientY };
            isScrollingRef.current = false;
        }
    }, []);

    // Handle touch move - detect if user is scrolling
    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        if (!touchStartRef.current) return;

        const touch = e.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
        const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
        const threshold = 10; // pixels - adjust if needed

        // If movement exceeds threshold, user is scrolling
        if (deltaX > threshold || deltaY > threshold) {
            isScrollingRef.current = true;
        }
    }, []);

    // Handle navigation - only navigate if it was a tap, not a scroll
    const handleNavigation = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const isTouch = 'touches' in e || 'changedTouches' in e;

            if (isTouch || window.innerWidth < 768) {
                // If it was a scroll gesture, don't navigate
                if (isScrollingRef.current) {
                    touchStartRef.current = null;
                    isScrollingRef.current = false;
                    return;
                }

                e.preventDefault();
                e.stopPropagation();
                router.visit(`/leads/${lead.id}`, {
                    preserveState: false,
                    preserveScroll: false,
                });
            }

            // Reset refs after handling
            touchStartRef.current = null;
            isScrollingRef.current = false;
        },
        [lead.id],
    );

    // Ensure we have a valid ID
    if (!lead?.id) {
        console.error('Lead ID is missing:', lead);
        return null;
    }

    return (
        <Link
            href={`/leads/${lead.id}`}
            className="touch-target block"
            onClick={handleNavigation}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleNavigation}
        >
            <Card className="mb-2 cursor-pointer transition-shadow hover:shadow-sm">
                <CardContent className="p-3">
                    <div className="flex flex-col gap-2">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 flex-1">
                                <h3 className="text-sm font-semibold">{leadNumber}</h3>
                                {lead.name && <p className="text-muted-foreground mt-0.5 text-xs">{lead.name}</p>}
                            </div>
                            <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5">
                                <Badge variant="outline" className={`px-1.5 py-0 text-xs ${getStatusConfig(lead.status).color}`}>
                                    {getStatusConfig(lead.status).label}
                                </Badge>
                                {lead.priority &&
                                    (() => {
                                        const priorityConfig = getPriorityConfig(lead.priority);
                                        return (
                                            <Badge variant="outline" className={`px-1.5 py-0 text-xs ${priorityConfig.color}`}>
                                                {priorityConfig.icon && <span className="mr-1">{priorityConfig.icon}</span>}
                                                {priorityConfig.label}
                                            </Badge>
                                        );
                                    })()}
                            </div>
                        </div>

                        {/* Details */}
                        <div className="space-y-1">
                            {value > 0 && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <DollarSign className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>{formatCurrency(value)}</span>
                                </div>
                            )}

                            {lead.company && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Building2 className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{lead.company.name}</span>
                                </div>
                            )}

                            {lead.person && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <User className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">
                                        {lead.person.first_name} {lead.person.last_name}
                                    </span>
                                </div>
                            )}

                            {lead.address && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="truncate">{lead.address}</span>
                                </div>
                            )}

                            {lead.last_contacted && (
                                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                                    <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span>
                                        {t('leads.tables.last_contacted', 'Last contacted')}: {new Date(lead.last_contacted).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Link>
    );
}
